import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CheckCircle2, Download, Film, Loader2, Sparkles, XCircle } from 'lucide-react'

import { brand } from '@/brand/brand'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { apiJson, apiFetch } from '@/lib/api'
import type { AspectRatio } from '@/types'
import type { GenerationMode, VideoMode } from '@/components/create/StepSetup'
import { MODELS_WITH_FAST_VARIANT, VIDEO_MODE_TO_FILMOGRAPHY } from '@/components/create/StepSetup'
import { TextOverlayEditor } from '@/components/create/TextOverlayEditor'
import { useToast } from '@/components/ui/use-toast'

interface Props {
  scriptId: number
  modelId: string
  aspectRatio: AspectRatio
  avatarAssetId: number | null
  /** All reference image asset IDs (up to 3). When present, forwarded as image_urls to the assembly endpoint. */
  refImageIds?: number[]
  contextFolderIds: number[]
  styleReferenceIds: number[]
  generationMode: GenerationMode
  videoMode: VideoMode
  /** Auto-stitch all scenes into one final video. Default ON for wizard flows. */
  autoStitch?: boolean
  /** Crossfade duration between scenes at stitch time. */
  transitionSeconds?: number
  /** Task 8: fast preview mode. When true the assembly endpoint applies the
   *  fast-variant + 480p + 5s caps + auto-stitch=off transforms server-side. */
  fastPreview?: boolean
  /** Bridge toggle — when true every scene chains to the previous scene's
   *  last frame to lock identity, outfit, and setting across cuts. */
  bridgeEnabled?: boolean
  audioUrl: string
  voiceId: string
  durationSeconds: number
  generationParams: Record<string, any>
}

interface AssemblyResponse {
  id: number
  name: string
  status: string
  scenes?: { scene_number: number; status: string; output_url: string | null; generation_job_id?: number | null }[]
  output_path?: string | null
  /** Task 10: post-stitch text overlays (auto-suggested when a storyboard
   *  plan exists; user can edit/add/remove via the TextOverlayEditor). */
  text_overlays?: import('@/types').TextOverlayBlock[] | null
  /** Task 10: sum of scene durations on success — bounds the timing inputs. */
  total_duration_seconds?: number | null
}

/** Returns entries from generationParams whose value differs from null/undefined/empty. */
function nonDefaultEntries(params: Record<string, any>): [string, any][] {
  return Object.entries(params).filter(
    ([, v]) => v !== null && v !== undefined && v !== '',
  )
}

/** Normalize a stored video path to a usable browser URL.
 *
 *  Backend stores per-scene URLs as "/uploads/generated/abc.mp4" but
 *  stitched output_path as "uploads/assemblies/.../stitched.mp4" (no
 *  leading slash). Make either work without double-prefixing.
 */
function videoSrc(pathOrUrl: string | null | undefined): string {
  if (!pathOrUrl) return ''
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) return pathOrUrl
  if (pathOrUrl.startsWith('/uploads/')) return pathOrUrl
  if (pathOrUrl.startsWith('uploads/')) return '/' + pathOrUrl
  return '/uploads/' + pathOrUrl.replace(/^\/+/, '')
}

function aspectVideoClass(aspectRatio: AspectRatio): string {
  if (aspectRatio === '9:16' || aspectRatio === '3:4') return 'mx-auto max-h-[60vh] w-auto block bg-black'
  if (aspectRatio === '1:1') return 'w-full aspect-square object-contain bg-black'
  return 'w-full aspect-video object-contain bg-black'
}


// ─── Task 12: Facial fallback panel ──────────────────────────────────────
// Small inline panel that lets the user re-run the same script with a
// face-strong model when Seedance's face came out wrong. Backend picks
// the model (OmniHuman 1.5 by default); this component just dispatches
// and swaps the wizard's local assembly state to the new row so the
// progress + result UI tracks the fallback render in-place. The
// original assembly stays in the user's gallery for comparison.

interface FacialFallbackPanelProps {
  sourceAssemblyId: number
  onDispatched: (newAssembly: AssemblyResponse) => void
}

function FacialFallbackPanel({ sourceAssemblyId, onDispatched }: FacialFallbackPanelProps) {
  const { toast } = useToast()
  const [submitting, setSubmitting] = React.useState(false)

  const handleClick = async () => {
    const ok = window.confirm(
      'Face came out wrong with Seedance?\n\n' +
      'This re-runs the SAME script using OmniHuman 1.5 — a model trained ' +
      'specifically to hold face identity through motion. Cost ≈ $0.10–0.30 ' +
      'per generated second.\n\n' +
      'Your original video stays in the gallery for side-by-side comparison.',
    )
    if (!ok) return
    setSubmitting(true)
    try {
      const data = await apiJson<{
        source_assembly_id: number
        new_assembly_id: number
        model_id: string
        status: string
      }>(
        `/api/v1/assembly/${sourceAssemblyId}/retry-with-fallback`,
        { method: 'POST', body: JSON.stringify({}) },
      )
      // Pull the fresh new assembly so the wizard's polling lifecycle
      // transitions into tracking the fallback generation.
      const next = await apiJson<AssemblyResponse>(`/api/v1/assembly/${data.new_assembly_id}`)
      onDispatched(next)
      toast({
        title: 'Facial fallback dispatched',
        description: 'Generating with OmniHuman 1.5. Watch progress here; original is in the gallery.',
      })
    } catch (err) {
      toast({
        title: 'Could not dispatch facial fallback',
        description: (err as Error).message,
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card className="border-dashed">
      <CardContent className="py-3 px-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px] space-y-0.5">
          <p className="text-sm font-medium">Face came out wrong?</p>
          <p className="text-xs text-muted-foreground">
            Seedance can drift on multi-second talking heads. Re-render the
            same script with <span className="font-medium">OmniHuman 1.5</span> — a
            model trained to hold face identity through motion.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleClick}
          disabled={submitting}
        >
          {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Retry with facial fallback
        </Button>
      </CardContent>
    </Card>
  )
}

export function StepGenerate({
  scriptId,
  modelId,
  aspectRatio,
  avatarAssetId,
  refImageIds,
  contextFolderIds,
  styleReferenceIds,
  generationMode,
  videoMode,
  autoStitch,
  transitionSeconds,
  fastPreview,
  bridgeEnabled,
  audioUrl,
  voiceId,
  durationSeconds,
  generationParams,
}: Props) {
  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [assembly, setAssembly] = React.useState<AssemblyResponse | null>(null)
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null)

  // Per-scene styles preview — fetched from the script so the Summary card
  // can show the exact per-scene assignments (e.g. "Scene 1: ugc, Scene 2:
  // lifestyle"). Empty array = global videoMode applies to every scene.
  const [perSceneStyles, setPerSceneStyles] = React.useState<string[]>([])
  React.useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        type ScriptDetail = {
          active_version: {
            scenes?: Array<{ scene_type?: string | null }>
            meta?: { scene_video_modes?: string[] | null } | null
          } | null
        }
        const data = await apiJson<ScriptDetail>(`/api/v1/scripts/${scriptId}`)
        if (cancelled) return
        const fromMeta = data.active_version?.meta?.scene_video_modes ?? null
        const fromScenes = (data.active_version?.scenes ?? [])
          .map((s) => s.scene_type ?? null)
          .filter((m): m is string => typeof m === 'string' && m.length > 0)
        setPerSceneStyles((fromMeta && fromMeta.length > 0) ? fromMeta : fromScenes)
      } catch {
        /* best-effort — summary still renders without per-scene styles. */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [scriptId])

  const hasMixedScenes =
    perSceneStyles.length > 1 &&
    perSceneStyles.some((m) => m !== perSceneStyles[0])

  // Poll assembly status every 5s until a settled state.
  // Settled states: completed (per-scene done, no stitch), stitched
  // (combined cut exists), failed. Transient states keep polling:
  // 'stitching' (waiting for ffmpeg concat) and 'applying_overlays'
  // (Task 10 — ffmpeg drawtext pass running).
  React.useEffect(() => {
    const settled = ['completed', 'stitched', 'failed']
    if (!assembly || settled.includes(assembly.status)) {
      if (pollRef.current) clearInterval(pollRef.current)
      return
    }
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiFetch(`/api/v1/assembly/${assembly.id}`)
        if (res.ok) {
          const data: AssemblyResponse = await res.json()
          setAssembly(data)
          if (settled.includes(data.status)) {
            if (pollRef.current) clearInterval(pollRef.current)
          }
        }
      } catch { /* ignore poll errors */ }
    }, 5000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [assembly?.id, assembly?.status])

  // Immediate-refresh helper for the TextOverlayEditor. It calls this after
  // a PATCH (save) or POST (apply-overlays) so the user doesn't wait for the
  // 5s poll tick to see the status flip into 'applying_overlays' or the
  // refreshed output_path once the titled video lands.
  const refreshAssembly = React.useCallback(async () => {
    if (!assembly?.id) return
    try {
      const res = await apiFetch(`/api/v1/assembly/${assembly.id}`)
      if (res.ok) {
        const data: AssemblyResponse = await res.json()
        setAssembly(data)
      }
    } catch { /* swallow — poll will retry */ }
  }, [assembly?.id])

  const handleGenerate = async () => {
    setLoading(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        script_id: scriptId,
        model_id: modelId,
        aspect_ratio: aspectRatio,
        video_mode: videoMode,
        // Task 1 (multi-scene fix): map the wizard's videoMode to the
        // backend style_profile id so the assembly applies a common
        // filmography to every scene. Auto-stitch defaults to true so the
        // gallery gets one stitched ad instead of three separate clips.
        filmography_profile: VIDEO_MODE_TO_FILMOGRAPHY[videoMode],
        auto_stitch: autoStitch !== false,
        transition_seconds: transitionSeconds ?? 0.3,
        // Task 8: fast preview flag. Backend applies the model swap +
        // resolution + duration + auto_stitch=false transforms when true,
        // so the request body intentionally still carries the user's
        // chosen modelId / autoStitch — server-side preview overrides win.
        fast_preview: fastPreview === true,
        // Bridge toggle — chains each scene to the previous scene's last frame.
        bridge_enabled: bridgeEnabled === true,
        avatar_asset_id: avatarAssetId ?? undefined,
        // Pass all selected reference image ids (back-compat: single id still present via avatar_asset_id)
        ...(refImageIds && refImageIds.length > 0 ? { ref_image_ids: refImageIds } : {}),
        context_folder_ids: contextFolderIds.length > 0 ? contextFolderIds : undefined,
        style_reference_ids: styleReferenceIds.length > 0 ? styleReferenceIds : undefined,
        generation_params: {
          ...(Object.keys(generationParams).length > 0 ? generationParams : {}),
          ...(voiceId ? { voice_id: voiceId } : {}),
        },
      }
      if (generationMode === 'avatar' && audioUrl.trim()) {
        body.audio_urls = [audioUrl.trim()]
      }
      const data = await apiJson<AssemblyResponse>('/api/v1/assembly/from-script', {
        method: 'POST',
        body: JSON.stringify(body),
      })
      setAssembly(data)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const advancedEntries = nonDefaultEntries(generationParams)

  // First scene's job id — available once Celery task runs (via polling)
  const firstJobId = assembly?.scenes?.find((s) => s.generation_job_id)?.generation_job_id ?? null

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-5">
        <Card>
          <CardContent className="py-20 text-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
            <div className="space-y-1">
              <p className="text-sm font-medium">Queuing generation...</p>
              <p className="text-xs text-muted-foreground">Submitting your ad to the pipeline</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-4 text-sm text-destructive space-y-3">
          <p className="font-semibold">Generation failed to start</p>
          <p className="text-destructive/80">{error}</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setError(null)}>
          ← Try again
        </Button>
      </div>
    )
  }

  if (assembly) {
    // Status taxonomy:
    //   generating | completed (per-scene done, no stitch) | stitching | stitched (combined cut ready) | failed
    const isStitching = assembly.status === 'stitching'
    const isStitched = assembly.status === 'stitched'
    const isCompleted = assembly.status === 'completed' || isStitched
    const isFailed = assembly.status === 'failed'
    // "Terminal for the per-scene generation phase" — used by the spinner /
    // progress bar. Stitching is its own phase below.
    const isGenerationTerminal = isCompleted || isFailed
    const scenes = (assembly.scenes || []).slice().sort(
      (a, b) => a.scene_number - b.scene_number,
    )
    const completedScenes = scenes.filter((s) => s.status === 'completed').length
    const totalScenes = scenes.length || 1
    const progressPct = isGenerationTerminal ? 100 : Math.round((completedScenes / totalScenes) * 100)
    const completedSceneOutputs = scenes.filter((s) => s.status === 'completed' && s.output_url)
    const stitchedUrl = assembly.output_path

    // Pipeline step labels for avatar mode
    const pipelineSteps = generationMode === 'avatar'
      ? ['Generating voiceover (TTS)', 'Creating avatar video', 'Syncing lip movements']
      : null

    // Model display name
    const modelName = modelId.split('/').pop()?.replace(/-/g, ' ') ?? modelId

    const handleStitch = async () => {
      try {
        const data = await apiJson<AssemblyResponse>(
          `/api/v1/assembly/${assembly.id}/stitch`,
          { method: 'POST' },
        )
        // Re-prime polling: status flips to 'stitching', the existing
        // poll effect kicks back in because !['completed','failed','stitched']
        setAssembly(data)
      } catch (err) {
        setError((err as Error).message)
      }
    }

    return (
      <div className="max-w-3xl mx-auto space-y-5">
        <Card>
          <CardContent className="pt-5 space-y-5">
            {/* Top row: status + View Jobs */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                {!isGenerationTerminal && <Loader2 className="h-5 w-5 animate-spin" />}
                {isCompleted && !isStitching && <CheckCircle2 className="h-5 w-5 text-green-500" />}
                {isStitching && <Loader2 className="h-5 w-5 animate-spin" />}
                {isFailed && <XCircle className="h-5 w-5 text-destructive" />}
                <div>
                  <p className="text-sm font-semibold">
                    {isStitching
                      ? 'Combining scenes...'
                      : isStitched
                      ? 'Combined cut ready'
                      : isCompleted
                      ? 'All scenes ready'
                      : isFailed
                      ? 'Failed'
                      : 'Generating'}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {modelName} · {aspectRatio} · {durationSeconds}s
                    {!isGenerationTerminal && (
                      <span className="ml-1 opacity-60">
                        · ~{generationMode === 'avatar'
                          ? `${Math.ceil(durationSeconds * 0.5)}–${Math.ceil(durationSeconds * 1.2)} min`
                          : `${Math.ceil(totalScenes * 2)}–${Math.ceil(totalScenes * 5)} min`
                        }
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {firstJobId && (
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/created-assets/${firstJobId}`}>View asset #{firstJobId}</Link>
                  </Button>
                )}
                <Button variant="outline" size="sm" asChild>
                  <Link to="/created-assets">All assets</Link>
                </Button>
              </div>
            </div>

            {/* Progress bar */}
            <Progress value={progressPct} className="h-1" />

            {/* Pipeline timeline (avatar mode) */}
            {pipelineSteps && !isGenerationTerminal && (
              <div className="space-y-2">
                {pipelineSteps.map((step, i) => {
                  const isActive = i === 0 // simplified — step 1 always active during generation
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ${
                        isActive
                          ? 'bg-foreground text-background'
                          : 'border border-border text-muted-foreground'
                      }`}>
                        {isActive ? <Loader2 className="h-3 w-3 animate-spin" /> : i + 1}
                      </div>
                      <span className={`text-sm ${isActive ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                        {step}
                      </span>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Per-scene status (multi-scene only) — kept while still generating */}
            {scenes.length > 1 && !isGenerationTerminal && (
              <div className="space-y-1.5 border-t border-border pt-3">
                {scenes.map((s) => (
                  <div key={s.scene_number} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Scene {s.scene_number}</span>
                    <div className="flex items-center gap-1.5">
                      {s.status === 'completed' && <CheckCircle2 className="h-3 w-3 text-green-500" />}
                      {s.status === 'failed' && <XCircle className="h-3 w-3 text-destructive" />}
                      {['queued', 'processing', 'pending'].includes(s.status) && (
                        <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                      )}
                      <span className="capitalize">{s.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* While generating: pulsing logo placeholder */}
        {!isGenerationTerminal && !isFailed && (
          <Card className="overflow-hidden">
            <CardContent className="py-20 text-center space-y-5">
              <div className="flex flex-col items-center gap-4">
                <img
                  src={brand.logoUrl}
                  alt={brand.company}
                  className="h-16 w-auto animate-pulse"
                />
                <div className="space-y-1.5">
                  <p className="text-sm font-medium text-primary">
                    Rendering your video...
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {generationMode === 'avatar'
                      ? `Estimated: ${Math.ceil(durationSeconds * 0.5)}–${Math.ceil(durationSeconds * 1.2)} min (TTS + avatar + lip-sync)`
                      : `Estimated: ${Math.ceil(totalScenes * 2)}–${Math.ceil(totalScenes * 5)} min (${totalScenes} scene${totalScenes > 1 ? 's' : ''})`
                    }
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="h-1.5 w-1.5 rounded-full"
                      style={{
                        backgroundColor: '#D4A44C',
                        animation: `pulse 1.5s ease-in-out ${i * 0.3}s infinite`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Failure */}
        {isFailed && (
          <Card>
            <CardContent className="py-12 text-center space-y-3">
              <XCircle className="h-8 w-8 text-destructive/40 mx-auto" />
              <p className="text-sm text-muted-foreground">
                {assembly.status === 'failed' ? 'Generation failed' : 'An error occurred'}
              </p>
              <Button variant="outline" size="sm" asChild>
                <Link to="/created-assets">View error details</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Per-scene grid — primary deliverable */}
        {isCompleted && completedSceneOutputs.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">
                {completedSceneOutputs.length === 1
                  ? 'Your video'
                  : `Your videos (${completedSceneOutputs.length} scenes)`}
              </h3>
              {completedSceneOutputs.length > 1 && !isStitched && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleStitch}
                  disabled={isStitching}
                >
                  {isStitching ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                      Combining...
                    </>
                  ) : (
                    <>
                      <Film className="h-3.5 w-3.5 mr-1.5" />
                      Combine into one cut
                    </>
                  )}
                </Button>
              )}
            </div>

            <div className={
              completedSceneOutputs.length === 1
                ? ''
                : 'grid grid-cols-1 sm:grid-cols-2 gap-3'
            }>
              {completedSceneOutputs.map((s) => (
                <Card key={s.scene_number} className="overflow-hidden">
                  <video
                    src={videoSrc(s.output_url)}
                    controls
                    className={aspectVideoClass(aspectRatio)}
                  />
                  <CardContent className="py-3 flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {completedSceneOutputs.length > 1 ? `Scene ${s.scene_number}` : `${durationSeconds}s clip`}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      asChild
                    >
                      <a href={videoSrc(s.output_url)} download>
                        <Download className="h-3.5 w-3.5 mr-1" />
                        Download
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Combined cut — present only after explicit stitch (or legacy stitched assemblies) */}
        {(isStitched || assembly.status === 'applying_overlays') && stitchedUrl && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Combined cut</h3>
              <Button
                size="sm"
                variant="ghost"
                asChild
              >
                <a href={videoSrc(stitchedUrl)} download>
                  <Download className="h-3.5 w-3.5 mr-1" />
                  Download
                </a>
              </Button>
            </div>
            <Card className="overflow-hidden">
              <video
                // src key forces React to remount the <video> when the URL
                // changes (e.g. stitched.mp4 → stitched_titled.mp4 after
                // overlays apply) — otherwise some browsers keep the old
                // buffered source.
                key={stitchedUrl ?? ''}
                src={videoSrc(stitchedUrl)}
                controls
                autoPlay
                className={aspectVideoClass(aspectRatio)}
              />
            </Card>

            {/* Task 10: text overlay editor. Lives here because the user
                needs to have watched the stitched video before setting
                meaningful timing. Auto-suggested CTA from storyboard plan
                pre-populates `assembly.text_overlays` server-side. */}
            <TextOverlayEditor
              assemblyId={assembly.id}
              initialOverlays={assembly.text_overlays}
              totalDuration={assembly.total_duration_seconds ?? 0}
              status={assembly.status}
              onChanged={refreshAssembly}
            />

            {/* Task 12: facial fallback. Visible only when a face is in
                frame (avatarAssetId set) AND the current model is a
                Seedance variant — matches the backend recommendation
                rule. Click → confirm modal → POST creates a new
                assembly with OmniHuman; local state swaps to the new
                row so the user sees the fallback generation kick off
                in-place. */}
            {avatarAssetId !== null && modelId.includes('seedance') && (
              <FacialFallbackPanel
                sourceAssemblyId={assembly.id}
                onDispatched={(newAssembly) => setAssembly(newAssembly)}
              />
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="space-y-1">
        <h2 className="text-xl font-semibold">Ready to generate</h2>
        <p className="text-sm text-muted-foreground">
          Review your setup and kick off the video generation pipeline.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Script ID</span>
            <span className="font-mono">{scriptId}</span>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Mode</span>
            <Badge variant="secondary" className="capitalize">
              {generationMode === 'avatar' ? 'Talking head / Avatar' : 'Product Video'}
            </Badge>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Video style</span>
            <Badge variant="outline" className="text-xs font-mono">
              {videoMode}
            </Badge>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Model</span>
            <span className="font-mono">{modelId || '—'}</span>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Aspect ratio</span>
            <span className="font-medium">{aspectRatio}</span>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Duration</span>
            <span className="font-medium">{durationSeconds}s</span>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <span
              className="text-muted-foreground"
              title="When ON, every scene starts from the previous scene's last frame so the same person, outfit and setting carry across cuts. Sequential dispatch — slower but coherent."
            >
              Same person across scenes (bridge)
            </span>
            <Badge variant={bridgeEnabled ? 'default' : 'outline'}>
              {bridgeEnabled ? 'On' : 'Off'}
            </Badge>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <span
              className="text-muted-foreground"
              title="Fast preview swaps the model to its fast variant, caps resolution to 480p, caps each scene to 5s, and disables auto-stitch."
            >
              Fast preview
            </span>
            <Badge variant={fastPreview ? 'default' : 'outline'}>
              {fastPreview ? 'On' : 'Off'}
            </Badge>
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Auto-stitch final cut</span>
            <Badge variant={autoStitch !== false ? 'default' : 'outline'}>
              {autoStitch !== false ? 'On' : 'Off'}
            </Badge>
          </div>
          {transitionSeconds != null && autoStitch !== false && (
            <>
              <Separator />
              <div className="flex items-center justify-between">
                <span
                  className="text-muted-foreground"
                  title="Crossfade duration between adjacent scenes at stitch time. 0 = hard cuts."
                >
                  Scene transition
                </span>
                <span className="font-medium">
                  {transitionSeconds === 0 ? 'Hard cut' : `${transitionSeconds}s crossfade`}
                </span>
              </div>
            </>
          )}
          {hasMixedScenes && (
            <>
              <Separator />
              <div className="flex items-start justify-between gap-3">
                <span className="text-muted-foreground shrink-0">Per-scene styles</span>
                <div className="flex gap-1 flex-wrap justify-end">
                  {perSceneStyles.map((mode, i) => (
                    <Badge
                      key={`scene-${i}`}
                      variant="outline"
                      className="text-[10px] font-mono"
                      title={`Scene ${i + 1}: ${mode}`}
                    >
                      S{i + 1}: {mode}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}
          {(refImageIds && refImageIds.length > 0 ? refImageIds : avatarAssetId ? [avatarAssetId] : []).length > 0 && (
            <>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  Reference image{(refImageIds && refImageIds.length > 1) ? 's' : ''}
                </span>
                <div className="flex gap-1 flex-wrap justify-end">
                  {(refImageIds && refImageIds.length > 0 ? refImageIds : avatarAssetId ? [avatarAssetId] : []).map((id) => (
                    <Badge key={id} variant="secondary" className="text-xs font-mono">
                      #{id}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}
          {generationMode === 'avatar' && audioUrl.trim() && (
            <>
              <Separator />
              <div className="flex items-start justify-between gap-3">
                <span className="text-muted-foreground shrink-0">Voice audio</span>
                <span className="font-mono text-xs truncate max-w-[240px]" title={audioUrl}>
                  {audioUrl}
                </span>
              </div>
            </>
          )}
          {contextFolderIds.length > 0 && (
            <>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Context folders</span>
                <div className="flex gap-1 flex-wrap justify-end">
                  {contextFolderIds.map((id) => (
                    <Badge key={id} variant="secondary" className="text-xs">
                      #{id}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}
          {styleReferenceIds.length > 0 && (
            <>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Style references</span>
                <div className="flex gap-1 flex-wrap justify-end">
                  {styleReferenceIds.map((id) => (
                    <Badge key={id} variant="secondary" className="text-xs">
                      #{id}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}
          {advancedEntries.length > 0 && (
            <>
              <Separator />
              <div className="flex items-start justify-between gap-3">
                <span className="text-muted-foreground shrink-0">Advanced params</span>
                <span className="font-mono text-xs text-right">
                  {advancedEntries
                    .map(([k, v]) => `${k}: ${String(v)}`)
                    .join(', ')}
                </span>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Warn when a reference-to-video model is selected but no reference image is set */}
      {modelId.includes('reference') && !avatarAssetId && (!refImageIds || refImageIds.length === 0) && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-600 dark:text-amber-400">
          <strong>No reference image selected.</strong> The model "{modelId.split('/').pop()}" works best with a product image. Go back to Step 1 and pick a reference image, or the video will be generated without product context.
        </div>
      )}

      {/* Task 8: Preview-mode reminder right above the Generate button so
          the user sees the trade-off at the moment of click. Server-side
          overrides apply regardless, but a visible banner prevents
          surprise when the output comes back at 480p. The cost claim is
          conditioned on whether a fast variant will actually be applied —
          for models without one, the user saves wall-clock but not
          per-second cost. */}
      {fastPreview === true && (() => {
        const hasFastVariant = MODELS_WITH_FAST_VARIANT.has(modelId)
        return (
          <div className="rounded-md border border-foreground/30 bg-foreground/5 px-4 py-3 text-sm">
            <strong>Preview render.</strong>{' '}
            {hasFastVariant
              ? 'Each scene will render at 480p, capped at 5s, fast model variant, no auto-stitch. Roughly 30–60s per scene at ~half the per-second cost.'
              : 'Each scene is capped at 5s with no auto-stitch. This model has no fast variant — the per-second cost stays the same; savings come from the duration cap only.'}{' '}
            Switch <em>Fast preview mode</em> off in Step 3 before
            rendering the final version.
          </div>
        )
      })()}

      <Button
        size="lg"
        className="w-full"
        onClick={handleGenerate}
        disabled={!modelId || !aspectRatio}
      >
        <Sparkles className="h-4 w-4" />
        {fastPreview === true ? 'Generate Preview' : 'Generate Ad'}
      </Button>
    </div>
  )
}
